/**
 * The template library, against `/api/note-templates`.
 *
 * Every route here is admin-only on the server (`requireOrgRole("org:admin")`),
 * which is why the Templates page checks the role before it renders rather than
 * letting six requests come back 403 one at a time.
 */
import { apiGet, apiSend } from '@/lib/api'
import type { NoteTemplate, ReasoningEffort } from './types'

/** What the server sends. Snake_case, and wider than this app renders. */
interface WireTemplate {
  id: string
  name: string
  description: string | null
  instructions: string | null
  model: string
  max_tokens: number
  reasoning_effort: string
  auto_generate: boolean
  share_by_default: boolean
  display_order: number
  is_system_default: boolean
  is_active: boolean
  updated_at: string
}

const EFFORTS: ReasoningEffort[] = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh']

function toTemplate(wire: WireTemplate): NoteTemplate {
  return {
    id: wire.id,
    name: wire.name,
    description: wire.description ?? '',
    instructions: wire.instructions ?? '',
    model: wire.model,
    maxTokens: wire.max_tokens,
    // An unknown effort from a newer server renders as `medium` rather than
    // putting a value in the select that the select cannot display.
    reasoningEffort: EFFORTS.includes(wire.reasoning_effort as ReasoningEffort)
      ? (wire.reasoning_effort as ReasoningEffort)
      : 'medium',
    autoGenerate: wire.auto_generate,
    shareByDefault: wire.share_by_default,
    displayOrder: wire.display_order,
    isSystemDefault: wire.is_system_default,
    isActive: wire.is_active,
    updatedAt: wire.updated_at,
  }
}

function toWire(draft: Partial<TemplateDraft>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (draft.name !== undefined) body.name = draft.name
  if (draft.description !== undefined) body.description = draft.description
  if (draft.instructions !== undefined) body.instructions = draft.instructions
  if (draft.model !== undefined) body.model = draft.model
  if (draft.maxTokens !== undefined) body.max_tokens = draft.maxTokens
  if (draft.reasoningEffort !== undefined) body.reasoning_effort = draft.reasoningEffort
  if (draft.autoGenerate !== undefined) body.auto_generate = draft.autoGenerate
  if (draft.shareByDefault !== undefined) body.share_by_default = draft.shareByDefault
  if (draft.displayOrder !== undefined) body.display_order = draft.displayOrder
  if (draft.isActive !== undefined) body.is_active = draft.isActive
  return body
}

function sorted(templates: NoteTemplate[]): NoteTemplate[] {
  return [...templates].sort(
    (left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name)
  )
}

/**
 * The last list the server sent.
 *
 * `templatesSync` and `findTemplate` resolve a template id while a row renders
 * — inside a popover, inside a virtualised list — and neither can await. The
 * cache is written by every read and write below, so it is current whenever
 * anything has loaded the library; before that it is empty and a row falls back
 * to the note's own title, which is what it did with no template anyway.
 */
let CACHE: NoteTemplate[] = []

export function templatesSync(activeOnly = true): NoteTemplate[] {
  return sorted(activeOnly ? CACHE.filter((item) => item.isActive) : CACHE)
}

export function findTemplate(id: string | null): NoteTemplate | undefined {
  if (!id) return undefined
  return CACHE.find((item) => item.id === id)
}

/** Replaces one entry in the cache, or appends it. */
function remember(template: NoteTemplate): NoteTemplate {
  const index = CACHE.findIndex((item) => item.id === template.id)
  if (index < 0) CACHE = [...CACHE, template]
  else CACHE = CACHE.map((item, at) => (at === index ? template : item))
  return template
}

export async function listTemplates(): Promise<NoteTemplate[]> {
  const { note_templates } = await apiGet<{ note_templates: WireTemplate[] }>('/api/note-templates')
  CACHE = note_templates.map(toTemplate)
  return sorted(CACHE)
}

export type TemplateDraft = Omit<NoteTemplate, 'id' | 'isSystemDefault' | 'updatedAt'>

/**
 * `auto_generate`, `share_by_default` and `display_order` are read back off the
 * draft, not off the response.
 *
 * The server stores them in a second table and writes them *after* it has
 * loaded the row it replies with, so a create answers with the defaults the
 * template had for the instant before the request finished — `autoGenerate:
 * true, displayOrder: 0` no matter what was sent. The write itself is correct,
 * and a re-read proves it; overlaying the draft avoids that extra round trip
 * while keeping the cache honest.
 */
export async function createTemplate(draft: TemplateDraft): Promise<NoteTemplate> {
  const { note_template } = await apiSend<{ note_template: WireTemplate }>(
    'POST',
    '/api/note-templates',
    toWire(draft)
  )
  return remember({
    ...toTemplate(note_template),
    autoGenerate: draft.autoGenerate,
    shareByDefault: draft.shareByDefault,
    displayOrder: draft.displayOrder,
  })
}

export async function updateTemplate(
  id: string,
  draft: Partial<TemplateDraft>
): Promise<NoteTemplate> {
  const { note_template } = await apiSend<{ note_template: WireTemplate }>(
    'PUT',
    `/api/note-templates/${id}`,
    toWire(draft)
  )
  return remember(toTemplate(note_template))
}

/**
 * Deletes an authored template; archives a shipped one.
 *
 * The server refuses to delete a system default outright (403), which is the
 * same judgement this app makes for its own reason: removing one would mean the
 * same install disagrees with the next about what the product is. Archiving
 * takes it out of every picker, which is the whole of what "delete" was asking
 * for — so the two paths are one action here and two requests underneath.
 */
export async function removeTemplate(id: string): Promise<void> {
  const template = CACHE.find((item) => item.id === id)
  if (template?.isSystemDefault) {
    await updateTemplate(id, { isActive: false })
    return
  }
  await apiSend<{ success: boolean }>('DELETE', `/api/note-templates/${id}`)
  CACHE = CACHE.filter((item) => item.id !== id)
}

/** A new template starts from the shape of the ones that ship, not from blank. */
export function emptyTemplate(): TemplateDraft {
  return {
    name: '',
    description: '',
    instructions: '',
    model: 'gpt-5.6-luna',
    maxTokens: 15_000,
    reasoningEffort: 'medium',
    autoGenerate: true,
    shareByDefault: false,
    displayOrder: (sorted(CACHE).at(-1)?.displayOrder ?? 0) + 10,
    isActive: true,
  }
}
